import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, VStack, HStack, Badge, Center, Spinner } from '@chakra-ui/react';
import {
  HiExclamationTriangle,
  HiShieldCheck,
  HiInformationCircle,
  HiArrowPath,
} from 'react-icons/hi2';
import { Button, TableOptions } from '@themes/aegix';
import { DrawerComponent } from './ui/DrawerComponent';
import { useNotifications, useMarkNotificationRead } from '@/hooks/useReunification';
import { useNotificationsDrawer } from '@/contexts/NotificationsDrawerContext';

const categoryConfig: Record<
  string,
  { icon: React.ElementType; color: string; bg: string }
> = {
  alert: { icon: HiExclamationTriangle, color: 'red.600', bg: 'red.100' },
  reunification: { icon: HiShieldCheck, color: 'orange.600', bg: 'orange.100' },
  status_update: { icon: HiArrowPath, color: 'blue.600', bg: 'blue.100' },
  info: { icon: HiInformationCircle, color: 'teal.600', bg: 'teal.100' },
};

const defaultConfig = { icon: HiInformationCircle, color: 'teal.600', bg: 'teal.100' };

function formatTimestamp(iso: string, t: (key: string, options?: any) => string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return t('notifications.minsAgo', { count: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('notifications.hoursAgo', { count: diffHr });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

interface NotificationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationsDrawer({ isOpen, onClose }: NotificationsDrawerProps) {
  const { t } = useTranslation();
  const [activeToggle, setActiveToggle] = useState('unread');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useNotifications({
    is_read: activeToggle === 'unread' ? false : undefined,
    page,
    page_size: 50,
  });
  const markRead = useMarkNotificationRead();
  const { localNotifications, markLocalNotificationRead } = useNotificationsDrawer();

  const serverItems = data?.results ?? [];
  const localItems = localNotifications.filter(
    (n) => activeToggle === 'all' || !n.is_read
  );

  const items = [...localItems, ...serverItems];
  const unreadCount =
    localNotifications.filter((n) => !n.is_read).length +
    serverItems.filter((n) => !n.is_read).length;

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t('notifications.title', { count: unreadCount })}
      footer={
        unreadCount > 0 ? (
          <Button
            visual="secondary"
            onClick={() => {
              serverItems
                .filter((n) => !n.is_read)
                .forEach((n) => markRead.mutate(n.id));
              localNotifications
                .filter((n) => !n.is_read)
                .forEach((n) => markLocalNotificationRead(n.id));
            }}
            width="100%"
          >
            {t('notifications.markAllRead')}
          </Button>
        ) : undefined
      }
    >
      <Box mx={-6}>
        <TableOptions
          toggleOptions={[
            { value: 'all', label: t('notifications.all') },
            { value: 'unread', label: t('notifications.unread') },
          ]}
          activeToggle={activeToggle}
          onToggleChange={(val) => { setActiveToggle(val); setPage(1); }}
        />
      </Box>

      {isLoading ? (
        <Center py={8}>
          <Spinner size="md" />
        </Center>
      ) : (
        <VStack gap={2} align="stretch">
          {items.map((notification) => {
            const config = categoryConfig[notification.category] ?? defaultConfig;
            const Icon = config.icon;

            const handleMarkRead = () => {
              if (typeof notification.id === 'string' && notification.id.startsWith('local-')) {
                markLocalNotificationRead(notification.id);
              } else {
                if (!notification.is_read) markRead.mutate(notification.id as number);
              }
            };

            return (
              <Box
                key={notification.id}
                role="button"
                tabIndex={0}
                aria-label={`${notification.is_read ? '' : t('notifications.unreadLabel') + ': '}${notification.title}`}
                bg={notification.is_read ? 'surface.primary' : 'surface.secondary'}
                border="1px solid"
                borderColor={notification.is_read ? 'border.secondary' : 'border.action'}
                borderRadius="lg"
                p={3}
                cursor="pointer"
                onClick={handleMarkRead}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMarkRead();
                  }
                }}
                transition="all 200ms"
                _hover={{ borderColor: 'border.action' }}
              >
                <HStack gap={3} align="start">
                  <Box
                    p={2}
                    borderRadius="full"
                    bg={config.bg}
                    color={config.color}
                    flexShrink={0}
                  >
                    <Icon size={16} />
                  </Box>
                  <Box flex={1}>
                    <HStack justify="space-between" mb={1}>
                      <HStack gap={2}>
                        <Text fontSize="sm" fontWeight="600" color="text.primary">
                          {notification.title}
                        </Text>
                        {!notification.is_read && (
                          <Badge
                            bg="red.500"
                            color="white"
                            borderRadius="full"
                            px={1.5}
                            fontSize="2xs"
                          >
                            {t('notifications.new')}
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="text.secondary" flexShrink={0}>
                        {formatTimestamp(notification.created_at, t)}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="text.secondary">
                      {notification.body}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            );
          })}

          {items.length === 0 && (
            <Box textAlign="center" py={8}>
              <Text fontSize="md" color="text.secondary">
                {t('notifications.empty')}
              </Text>
            </Box>
          )}
        </VStack>
      )}
    </DrawerComponent>
  );
}
